import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  LayoutGrid,
  User,
  Star,
  Clock,
  Briefcase,
  Scale,
  FlaskConical,
  HeartPulse,
  TrendingUp,
  Megaphone,
  GraduationCap,
  Wrench,
  Globe,
  Layers,
  Info,
  Cpu,
  FileText,
  Library,
  PenTool,
  Settings,
  Sparkles,
} from 'lucide-react';
import { getTypeDisplayName } from '../constants/smeCategories';

export const CategoryIconMap = {
  Business: Briefcase,
  Technology: Cpu,
  Law: Scale,
  Finance: TrendingUp,
  Science: FlaskConical,
  Healthcare: HeartPulse,
  Economics: TrendingUp,
  Marketing: Megaphone,
  Education: GraduationCap,
  Engineering: Wrench,
  Politics: Globe,
  Media: Layers,
  ArtsAndHumanities: Layers,
  Environment: Globe,
  SportsAndWellness: HeartPulse,
  Default: Info,
};

export const categoryColorMap = {
  Business: { bg: '#ffedd5', color: '#c2410c' },
  Technology: { bg: '#f3e8ff', color: '#7e22ce' },
  Law: { bg: '#e0e7ff', color: '#4338ca' },
  Finance: { bg: '#dcfce7', color: '#15803d' },
  Science: { bg: '#e0f2fe', color: '#0369a1' },
  Healthcare: { bg: '#ffe4e6', color: '#be123c' },
  Economics: { bg: '#fef9c3', color: '#a16207' },
  Marketing: { bg: '#fce7f3', color: '#be185d' },
  Education: { bg: '#cffafe', color: '#0e7490' },
  Engineering: { bg: '#ffedd5', color: '#c2410c' },
  Politics: { bg: '#f1f5f9', color: '#475569' },
  Media: { bg: '#f3e8ff', color: '#7e22ce' },
  ArtsAndHumanities: { bg: '#fce7f3', color: '#be185d' },
  Environment: { bg: '#dcfce7', color: '#15803d' },
  SportsAndWellness: { bg: '#ffe4e6', color: '#be123c' },
  Default: { bg: '#f1f5f9', color: '#475569' },
};

const smeNavItems = [
  { id: 'all', label: 'All SMEs', Icon: LayoutGrid },
  { id: 'my', label: 'My SMEs', Icon: User },
  { id: 'favorites', label: 'Favorites', Icon: Star },
  { id: 'recent', label: 'Recently Used', Icon: Clock },
];

const pageConfigs = {
  sme: {
    actionLabel: 'Create New SME',
    actionTo: '/sme/new',
    ActionIcon: Plus,
    sectionLabel: 'SME Views',
    navItems: smeNavItems,
    promoTitle: 'Build Smarter Content',
    promoDesc: 'Combine multiple AI SMEs to create high-quality, expert-driven content.',
    promoTo: '/generate-article',
    promoLabel: 'Learn how it works ->',
    showCategories: true,
  },
  editors: {
    actionLabel: 'Create Editor',
    actionTo: '/editor/new',
    ActionIcon: PenTool,
    sectionLabel: 'Editor Tools',
    navItems: [
      { id: '/editors', label: 'All Editors', Icon: PenTool, to: '/editors' },
      { id: '/editor/new', label: 'New Editor', Icon: Plus, to: '/editor/new' },
      { id: '/prompts', label: 'Prompt Manager', Icon: Settings, to: '/prompts' },
      { id: '/generate-article', label: 'Content Studio', Icon: Sparkles, to: '/generate-article' },
    ],
    promoTitle: 'Editorial Control',
    promoDesc: 'Use editor personas to shape tone, structure, and final article quality.',
    promoTo: '/generate-article',
    promoLabel: 'Generate with editors ->',
    showStyles: true,
  },
  studio: {
    actionLabel: 'Generate Article',
    actionTo: '/generate-article',
    ActionIcon: Sparkles,
    sectionLabel: 'Content Studio',
    navItems: [
      { id: '/generate-article', label: 'Article Generator', Icon: FileText, to: '/generate-article' },
      { id: '/', label: 'SME Library', Icon: User, to: '/' },
      { id: '/editors', label: 'Editors', Icon: PenTool, to: '/editors' },
      { id: '/prompts', label: 'Prompts', Icon: Settings, to: '/prompts' },
    ],
    promoTitle: 'Generation Inputs',
    promoDesc: 'Select SMEs for expertise and an editor for final voice before generating.',
    promoTo: '/',
    promoLabel: 'Review SMEs ->',
  },
  lexicon: {
    actionLabel: 'Open Lexicon',
    actionTo: '/lexicon',
    ActionIcon: Library,
    sectionLabel: 'Lexicon',
    navItems: [
      { id: '/lexicon', label: 'Workspace', Icon: Library, to: '/lexicon' },
      { id: '/', label: 'SME Library', Icon: User, to: '/' },
      { id: '/generate-article', label: 'Content Studio', Icon: Sparkles, to: '/generate-article' },
      { id: '/prompts', label: 'Prompts', Icon: Settings, to: '/prompts' },
    ],
    promoTitle: 'Reusable Summaries',
    promoDesc: 'Create and save concise topic summaries for future content workflows.',
    promoTo: '/generate-article',
    promoLabel: 'Use in content ->',
  },
  prompts: {
    actionLabel: 'Prompt Manager',
    actionTo: '/prompts',
    ActionIcon: Settings,
    sectionLabel: 'MANAGE PROMPTS',
    navItems: [
      { id: 'system-prompts', label: 'System Prompts', Icon: Settings, to: '/prompts?tab=system' },
      { id: 'sme-prompts', label: 'SME Prompts', Icon: User, to: '/prompts?tab=sme' },
      { id: 'editor-prompts', label: 'Editor Prompts', Icon: PenTool, to: '/prompts?tab=editor' },
    ],
    promoTitle: 'Prompt Governance',
    promoDesc: 'Ensure quality, accuracy and consistency in all prompts.',
    promoTo: '/prompts',
    promoLabel: 'Take you there ->',
  },
};

function getPageKey(pathname) {
  if (pathname.startsWith('/editor') || pathname.startsWith('/editors')) return 'editors';
  if (pathname.startsWith('/generate-article')) return 'studio';
  if (pathname.startsWith('/lexicon')) return 'lexicon';
  if (pathname.startsWith('/prompts')) return 'prompts';
  return 'sme';
}

const SMESidebar = ({ smes, editors, activeFilter, onFilterChange, pathname = '/' }) => {
  const navigate = useNavigate();
  const [allCategoriesOpen, setAllCategoriesOpen] = useState(false);
  const [allStylesOpen, setAllStylesOpen] = useState(false);
  const pageKey = getPageKey(pathname);
  const config = pageConfigs[pageKey] || pageConfigs.sme;
  const { ActionIcon } = config;

  const safeSmes = Array.isArray(smes) ? smes : [];
  const safeEditors = Array.isArray(editors) ? editors : [];

  const categoryCounts = safeSmes.reduce((acc, sme) => {
    const key = sme.type || 'Other';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const allCategories = Object.entries(categoryCounts)
    .sort((a, b) => b[1] - a[1])
    .map(([category, count]) => ({ category, count }));
  const topCategories = allCategories.slice(0, 6);

  const styleCounts = safeEditors.reduce((acc, editor) => {
    const key = editor.style || 'General';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const allStyles = Object.entries(styleCounts)
    .sort((a, b) => b[1] - a[1])
    .map(([style, count]) => ({ style, count }));
  const topStyles = allStyles.slice(0, 6);

  const getStyleColors = (style) => {
    const map = {
      Academic: { bg: '#eef2ff', color: '#4f46e5' },
      Journalistic: { bg: '#fff7ed', color: '#ea580c' },
      Creative: { bg: '#fdf2f8', color: '#db2777' },
      Technical: { bg: '#f1f5f9', color: '#475569' },
      Formal: { bg: '#eff6ff', color: '#2563eb' },
      Casual: { bg: '#ecfdf5', color: '#059669' },
    };
    return map[style] || { bg: '#f1f5f9', color: '#475569' };
  };

  const getStyleInitials = (style) => {
    if (!style) return '?';
    if (style.toLowerCase() === 'casual') return 'Cl';
    return style.substring(0, 1).toUpperCase();
  };

  const isRouteActive = (to) => {
    if (to === '/') return pathname === '/';
    const fullPath = pathname + window.location.search;
    if (to.includes('?')) {
      // If we are matching a query tab but search is empty, fallback to tab=system
      if (fullPath === '/prompts' && to === '/prompts?tab=system') return true;
      return fullPath === to;
    }
    return pathname === to || pathname.startsWith(`${to}/`);
  };

  return (
    <aside className="sme-sidebar">
      <div className="sidebar-sticky-header">
        <button className="sidebar-create-btn" onClick={() => navigate(config.actionTo)}>
          <ActionIcon size={16} strokeWidth={2.5} />
          {config.actionLabel}
        </button>
      </div>

      <nav className="sidebar-nav">
        <p className="sidebar-section-label">{config.sectionLabel}</p>
        {config.navItems.map(({ id, label, Icon, to }) => (
          <button
            key={id}
            className={`sidebar-nav-item ${
              pageKey === 'sme'
                ? activeFilter === id ? 'active' : ''
                : isRouteActive(to) ? 'active' : ''
            }`}
            onClick={() => {
              if (pageKey === 'sme') {
                onFilterChange(id);
              } else {
                navigate(to);
              }
            }}
          >
            <span className="sidebar-nav-icon">
              <Icon size={16} strokeWidth={2} />
            </span>
            <span>{label}</span>
          </button>
        ))}
      </nav>

      {config.showCategories && topCategories.length > 0 && (
        <div className="sidebar-categories">
          <p className="sidebar-section-label">Categories</p>
          {topCategories.map(({ category, count }) => {
            const colors = categoryColorMap[category] || categoryColorMap.Default;
            const CatIcon = CategoryIconMap[category] || CategoryIconMap.Default;
            const label = getTypeDisplayName(category);
            return (
              <button
                key={category}
                className={`sidebar-category-item ${activeFilter === category ? 'active' : ''}`}
                onClick={() => onFilterChange(category)}
              >
                <span
                  className="sidebar-cat-icon"
                  style={{ background: colors.bg, color: colors.color }}
                >
                  <CatIcon size={14} strokeWidth={2} />
                </span>
                <span className="sidebar-cat-label" title={label}>{label}</span>
                <span className="sidebar-cat-count">{count}</span>
              </button>
            );
          })}
          <button className="sidebar-view-all" onClick={() => setAllCategoriesOpen(prev => !prev)}>
            {allCategoriesOpen ? 'Hide categories' : 'View all categories'}
          </button>
          {allCategoriesOpen && (
            <div className="sidebar-all-categories">
              <div className="sidebar-all-categories-head">
                <span>All categories</span>
                <button onClick={() => onFilterChange('all')}>Clear</button>
              </div>
              {allCategories.map(({ category, count }) => {
                const colors = categoryColorMap[category] || categoryColorMap.Default;
                const CatIcon = CategoryIconMap[category] || CategoryIconMap.Default;
                const label = getTypeDisplayName(category);
                return (
                  <button
                    key={`all-${category}`}
                    className={`sidebar-all-category ${activeFilter === category ? 'active' : ''}`}
                    onClick={() => onFilterChange(category)}
                  >
                    <span
                      className="sidebar-cat-icon"
                      style={{ background: colors.bg, color: colors.color }}
                    >
                      <CatIcon size={14} strokeWidth={2} />
                    </span>
                    <span className="sidebar-cat-label" title={label}>{label}</span>
                    <span className="sidebar-cat-count">{count}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {config.showStyles && topStyles.length > 0 && (
        <div className="sidebar-categories">
          <p className="sidebar-section-label">Language Style</p>
          {topStyles.map(({ style, count }) => {
            const colors = getStyleColors(style);
            const initials = getStyleInitials(style);
            return (
              <button
                key={style}
                className={`sidebar-category-item ${activeFilter === style ? 'active' : ''}`}
                onClick={() => onFilterChange(style)}
              >
                <span
                  className="sidebar-cat-icon"
                  style={{ background: colors.bg, color: colors.color, fontWeight: '700', fontSize: '0.7rem' }}
                >
                  {initials}
                </span>
                <span className="sidebar-cat-label" title={style}>{style}</span>
                <span className="sidebar-cat-count">{count}</span>
              </button>
            );
          })}
          <button className="sidebar-view-all" onClick={() => setAllStylesOpen(prev => !prev)}>
            {allStylesOpen ? 'Hide styles' : 'View all styles'}
          </button>
          {allStylesOpen && (
            <div className="sidebar-all-categories">
              <div className="sidebar-all-categories-head">
                <span>All styles</span>
                <button onClick={() => onFilterChange('all')}>Clear</button>
              </div>
              {allStyles.map(({ style, count }) => {
                const colors = getStyleColors(style);
                const initials = getStyleInitials(style);
                return (
                  <button
                    key={`all-${style}`}
                    className={`sidebar-all-category ${activeFilter === style ? 'active' : ''}`}
                    onClick={() => onFilterChange(style)}
                  >
                    <span
                      className="sidebar-cat-icon"
                      style={{ background: colors.bg, color: colors.color, fontWeight: '700', fontSize: '0.7rem' }}
                    >
                      {initials}
                    </span>
                    <span className="sidebar-cat-label" title={style}>{style}</span>
                    <span className="sidebar-cat-count">{count}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="sidebar-promo">
        <div className="sidebar-promo-icon">
          <Layers size={32} strokeWidth={1.5} />
        </div>
        <p className="sidebar-promo-title">{config.promoTitle}</p>
        <p className="sidebar-promo-desc">{config.promoDesc}</p>
        <button className="sidebar-promo-link" onClick={() => navigate(config.promoTo)}>
          {config.promoLabel}
        </button>
      </div>
    </aside>
  );
};

export default SMESidebar;
